import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import img41 from "../../../assets/img41.jpg";
import { CheckCircle } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function DisabilityInsurance() {
  const container = "max-w-[1380px] mx-auto px-4 sm:px-6 md:px-10 lg:px-12";

  return (
    <>
      <SEO
        title="Assurance invalidité Canada – einsured.ca"
        description="Comparez les régimes d’assurance invalidité au Canada, évaluez les couvertures et choisissez celle qui protège votre revenu et votre sécurité financière."
        keywords="Assurance invalidité Canada, protection du revenu, invalidité de courte durée, assurance invalidité de longue durée, couverture propre occupation"
      />
      {/* HERO SECTION */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start text-center md:text-left"
        style={{ backgroundImage: `url(${img41})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>

        <div className={`relative z-10 w-full ${container}`}>
          <div className="text-white max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
              Assurance invalidité
            </h1>

            <p className="text-base sm:text-lg md:text-xl font-semibold mb-8">
              Remplacement du revenu en cas de maladie ou de blessure vous
              empêchant de travailler, pour maintenir votre style de vie
            </p>

            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition shadow-md text-base sm:text-lg"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* INTRO SECTION */}
      <section className="py-16 sm:py-20 md:py-20 bg-white">
        <div className={container}>
          <div className="max-w-5xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-10">
              Assurance invalidité
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-6">
              <span className="underline">L'assurance invalidité</span> vous verse
              directement des prestations si vous êtes dans l'impossibilité de
              travailler en raison d'une maladie ou d'un événement malheureux,
              comme un accident au travail vous empêchant de travailler pendant des
              mois, voire des années.
            </p>

            <h3 className="text-2xl sm:text-3xl font-bold mt-10 text-[#49628b] text-center mb-8">
              Pouvez-vous subvenir à vos besoins lorsque vous ne travaillez pas ?
            </h3>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-6">
              La plupart des gens se retrouveront rapidement à court d'argent sans
              revenu. C'est pourquoi{" "}
              <a
                href="/"
                className="text-[#00a8ff] font-semibold underline hover:text-[#0092e6] transition"
              >
                einsured.ca
              </a>{" "}
              propose une excellente assurance invalidité. Elle verse des
              prestations en cas d'accident au travail ou en dehors du travail.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-6">
              Combien de fois avez-vous vu un collègue se blesser au dos et devoir
              rester à la maison pendant des semaines ou des mois ? Parfois, ils ne
              peuvent pas reprendre le travail, perdent leur emploi et doivent
              trouver un autre moyen de gagner leur vie. C'est dans ces moments
              qu'une{" "}
              <span className="text-[#00a8ff] font-semibold">
                assurance invalidité
              </span>{" "}
              fiable au Canada peut apporter une réelle tranquillité d'esprit. Vous
              avez alors l'argent pour payer votre loyer ou votre hypothèque,
              acheter de la nourriture et payer vos traitements médicaux. Avoir cet
              argent vous permettra de récupérer pleinement et de reprendre le
              travail.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-6">
              L'assurance invalidité distribuée par einsured.ca au Canada peut vous
              offrir une protection garantie pour votre salaire. Pour trouver la
              soumission d'assurance invalidité la plus abordable au Canada, vous
              pouvez parler à l'un de nos experts en assurance. Nous offrons la
              police d'assurance invalidité la plus efficace et la plus pratique au
              Canada, qui protège votre bien-être en cas d'invalidité et peut vous
              soutenir financièrement tout au long de votre réadaptation.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8">
              Notre conseiller en assurance invalidité peut vous aider à choisir le
              bon régime en fonction de votre profession, de votre emploi et de vos
              facteurs de risque. Avec une police d'assurance invalidité efficace
              d'einsured.ca, vous pouvez protéger vos économies pendant votre
              absence du travail.
            </p>
          </div>
        </div>
      </section>

      {/* DEFINITION OF DISABILITY INSURANCE */}
      <section
        className="relative py-16 sm:py-20 md:py-24 bg-cover bg-center lg:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className={`relative z-10 ${container}`}>
          <div className="text-white text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6">
              Définition de l'assurance invalidité
            </h2>

            <p className="text-base sm:text-lg leading-relaxed max-w-4xl mx-auto mb-2">
              Dans{" "}
              <span className="text-[#00a8ff] font-semibold">
                l'assurance invalidité
              </span>
              , les termes « propre occupation », « toute occupation » et
              « occupation régulière » désignent différentes définitions
              d'invalidité, influençant le moment où l'assuré est admissible à
              recevoir des prestations.
            </p>
            <p className="text-base sm:text-lg leading-relaxed max-w-4xl mx-auto mb-8">
              Ces définitions aident à déterminer les circonstances dans lesquelles
              les prestations d'invalidité seront versées.
            </p>

            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-10">
              Voici une explication de chacune :
            </h3>

            {/* Cards */}
            <div className="grid gap-8 md:grid-cols-3 text-left">
              {/* Own Occupation */}
              <div className="bg-white/95 rounded-[32px] shadow-lg px-6 sm:px-8 py-8 sm:py-10 text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                  Propre occupation :
                </h4>
                <p className="text-base sm:text-lg leading-relaxed mb-4">
                  <span className="text-[#20a7df] font-semibold">
                    Définition :
                  </span>{" "}
                  « Propre occupation » signifie que l'assuré est considéré comme
                  invalide s'il est incapable d'exercer les fonctions de sa propre
                  profession — l'emploi qu'il occupait au moment où l'invalidité a
                  débuté.
                </p>
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="text-[#20a7df] font-semibold">
                    Implications :
                  </span>{" "}
                  Cette définition est plus libérale et tend à être favorable aux
                  titulaires de police, car elle reconnaît les compétences et
                  fonctions spécifiques associées à leur profession actuelle. Même
                  si l'assuré est capable de travailler dans un autre domaine, il
                  demeurerait admissible aux prestations s'il est incapable
                  d'exercer les fonctions de sa propre profession.
                </p>
              </div>

              {/* Any Occupation */}
              <div className="bg-white/95 rounded-[32px] shadow-lg px-6 sm:px-8 py-8 sm:py-10 text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                  Toute occupation :
                </h4>
                <p className="text-base sm:text-lg leading-relaxed mb-4">
                  <span className="text-[#20a7df] font-semibold">
                    Définition :
                  </span>{" "}
                  « Toute occupation » signifie que l'assuré est considéré comme
                  invalide s'il ne peut exercer les fonctions d'aucune profession
                  pour laquelle il est raisonnablement qualifié par son éducation,
                  sa formation ou son expérience.
                </p>
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="text-[#20a7df] font-semibold">
                    Implications :
                  </span>{" "}
                  Cette définition est plus restrictive et devient pertinente si
                  l'assuré est capable de travailler dans un autre domaine, même si
                  ce n'est pas le même que sa profession initiale. Elle établit un
                  seuil d'admissibilité plus élevé et peut entraîner la cessation
                  des prestations si l'assuré peut exercer un autre type de travail.
                </p>
              </div>

              {/* Regular Occupation */}
              <div className="bg-white/95 rounded-[32px] shadow-lg px-6 sm:px-8 py-8 sm:py-10 text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                  Occupation régulière (ou emploi régulier) :
                </h4>
                <p className="text-base sm:text-lg leading-relaxed mb-4">
                  <span className="text-[#20a7df] font-semibold">
                    Définition :
                  </span>{" "}
                  « Occupation régulière » est souvent utilisé de façon
                  interchangeable avec « propre occupation ». Cela signifie que
                  l'assuré est considéré comme invalide s'il ne peut exercer les
                  fonctions de sa profession habituelle ou propre.
                </p>
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="text-[#20a7df] font-semibold">
                    Implications :
                  </span>{" "}
                  La distinction entre « propre occupation » et « occupation
                  régulière » peut varier selon les polices, mais dans de nombreux
                  cas, elles signifient essentiellement la même chose — les
                  prestations sont versées si l'assuré ne peut exercer les fonctions
                  de l'emploi qu'il occupait au moment où l'invalidité a débuté.
                </p>
              </div>
            </div>

            <p className="mt-10 text-base sm:text-lg leading-relaxed max-w-5xl mx-auto text-left sm:text-center">
              Lors du choix d'une{" "}
              <span className="text-[#20a7df] font-semibold">
                police d'assurance invalidité
              </span>
              , il est essentiel pour les individus de comprendre et d'examiner
              attentivement la définition d'invalidité précisée dans la police. La
              définition peut avoir un impact significatif sur la portée de la
              couverture et les circonstances dans lesquelles les prestations seront
              versées. Les titulaires de police doivent tenir compte de leur
              profession spécifique, de leurs compétences et du niveau de protection
              souhaité lors du choix de la définition d'invalidité qui correspond le
              mieux à leurs besoins.
            </p>
          </div>
        </div>
      </section>

      {/* BASICS OF DISABILITY INSURANCE IN CANADA */}
      <section className="py-16 sm:py-20 md:py-20 bg-white">
        <div className={container}>
          <div className="text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-8">
              Les bases de l'assurance invalidité au Canada :
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-8">
              <span className="text-[#00a8ff] font-semibold">
                L'assurance invalidité
              </span>{" "}
              au Canada est conçue pour offrir une protection financière aux
              personnes incapables de travailler en raison d'une invalidité ou
              d'une maladie. Ce type d'assurance aide à remplacer une partie du
              revenu de la personne, lui permettant de respecter ses obligations
              financières même si elle est incapable de travailler. Voici les bases
              de l'assurance invalidité au Canada :
            </p>

            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#49628b] mb-6">
              Types d'assurance invalidité :
            </h3>

            <ul className="space-y-4 text-base sm:text-lg leading-7 sm:leading-8 mb-8">
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Invalidité de courte durée (ICD) :
                  </span>{" "}
                  Offre une couverture pour une courte période, généralement de
                  quelques semaines à quelques mois, et remplace un pourcentage du
                  revenu de la personne pendant cette période.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Invalidité de longue durée (ILD) :
                  </span>{" "}
                  Offre une couverture pour une période prolongée, souvent jusqu'à
                  ce que la personne atteigne l'âge de la retraite, et remplace une
                  partie du revenu si l'invalidité persiste.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Montant de la couverture :
                  </span>{" "}
                  Les polices d'assurance invalidité offrent généralement un
                  pourcentage du revenu prévenant l'invalidité comme prestation. Le
                  pourcentage exact peut varier, mais il est courant que les polices
                  remplacent environ 60 à 70 % du revenu de la personne.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Délai d'attente (période d'élimination) :
                  </span>{" "}
                  Il y a généralement un délai d'attente, également connu sous le
                  nom de période d'élimination, avant que les prestations
                  d'invalidité commencent. Cette période peut aller de quelques
                  jours à plusieurs mois, et les prestations sont versées après la
                  satisfaction du délai d'attente.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Définition de l'invalidité :
                  </span>{" "}
                  Les polices définissent l'invalidité de différentes façons.
                  Certaines utilisent une définition de « propre occupation », où
                  les prestations sont versées si la personne est incapable
                  d'exercer les fonctions de sa propre profession. D'autres
                  utilisent une définition de « toute occupation », où les
                  prestations sont versées si la personne ne peut exercer aucune
                  profession pour laquelle elle est raisonnablement qualifiée par
                  son éducation, sa formation ou son expérience.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Durée des prestations :
                  </span>{" "}
                  Les prestations d'invalidité de courte durée ont généralement une
                  durée maximale, allant souvent de quelques semaines à un an. Les
                  prestations d'invalidité de longue durée peuvent durer plusieurs
                  années ou jusqu'à ce que la personne atteigne un âge spécifié,
                  comme 65 ans.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">Primes :</span>{" "}
                  Les primes d'assurance invalidité sont influencées par des facteurs
                  tels que l'âge, la santé, la profession et le niveau de couverture
                  souhaité. Les primes peuvent être payées mensuellement ou
                  annuellement.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Couverture professionnelle :
                  </span>{" "}
                  Certaines personnes peuvent bénéficier d'une couverture
                  d'assurance invalidité par leur employeur dans le cadre d'un
                  régime d'avantages sociaux collectifs. D'autres peuvent choisir
                  de souscrire des polices individuelles d'assurance invalidité pour
                  une couverture supplémentaire ou s'ils sont travailleurs
                  autonomes.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Intégration avec d'autres prestations :
                  </span>{" "}
                  Les prestations d'invalidité peuvent être intégrées à d'autres
                  prestations, telles que les programmes gouvernementaux d'invalidité
                  ou les indemnisations des accidents du travail, pour s'assurer que
                  la prestation totale ne dépasse pas un certain pourcentage du
                  revenu prévenant l'invalidité.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Souscription médicale :
                  </span>{" "}
                  Pour obtenir une police individuelle d'assurance invalidité, les
                  demandeurs passent souvent par un processus de souscription
                  médicale, où leur état de santé et leurs antécédents médicaux sont
                  évalués pour déterminer l'admissibilité et les taux de prime.
                </p>
              </li>
            </ul>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-8">
              Comprendre les bases de l'assurance invalidité est essentiel pour les
              personnes souhaitant protéger leur revenu et leur bien-être financier
              en cas d'invalidité. Il est recommandé d'examiner attentivement les
              modalités de la police, de comparer les options et d'envisager de
              consulter un conseiller en assurance chez einsured.ca pour adapter la
              couverture à des besoins et circonstances spécifiques.
            </p>

            <div className="flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
              >
                Obtenez le meilleur taux
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CONSEQUENCES OF NOT BUYING DISABILITY INSURANCE */}
      <section className="py-16 sm:py-20 md:py-20 bg-gradient-to-b from-[#476a8f] via-[#3d85a6] to-[#46a7d4]">
        <div className={container}>
          <div className="text-white">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4">
              Conséquences de ne pas souscrire une assurance invalidité au Canada
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center mb-8 max-w-3xl mx-auto">
              Si vous ne souscrivez pas d'assurance invalidité au Canada et que
              vous devenez incapable de travailler en raison d'une invalidité ou
              d'une maladie, vous pourriez faire face à d'importants défis
              financiers.
            </p>

            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-10">
              Voici quelques conséquences potentielles de ne pas avoir d'assurance
              invalidité :
            </h3>

            {/* Cards grid */}
            <div className="grid gap-8 md:grid-cols-3 text-left">
              {/* Loss of Income */}
              <div className="bg-[#3f6f8f]/90 rounded-[32px] shadow-xl shadow-slate-900/30 px-6 sm:px-8 py-8 sm:py-10">
                <h4 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                  Perte de revenu :
                </h4>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 mt-1 text-[#00e0ff] shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    L'impact le plus immédiat est la perte de revenu si vous êtes
                    incapable de travailler. Sans assurance invalidité, vous devrez
                    peut-être vous fier uniquement à vos économies personnelles ou
                    à d'autres ressources financières pour couvrir vos dépenses
                    courantes.
                  </p>
                </div>
              </div>

              {/* Financial Strain */}
              <div className="bg-[#3f6f8f]/90 rounded-[32px] shadow-xl shadow-slate-900/30 px-6 sm:px-8 py-8 sm:py-10">
                <h4 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                  Difficultés financières :
                </h4>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 mt-1 text-[#00e0ff] shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    L'absence d'assurance invalidité peut entraîner des difficultés
                    financières, car vous pourriez avoir du mal à couvrir les
                    dépenses quotidiennes telles que les versements hypothécaires ou
                    de loyer, les factures de services publics, l'épicerie et
                    d'autres nécessités.
                  </p>
                </div>
              </div>

              {/* Depletion of Savings */}
              <div className="bg-[#3f6f8f]/90 rounded-[32px] shadow-xl shadow-slate-900/30 px-6 sm:px-8 py-8 sm:py-10">
                <h4 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                  Épuisement des économies :
                </h4>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 mt-1 text-[#00e0ff] shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    Sans source de revenu de remplacement, vous pourriez devoir
                    puiser dans vos économies pour couvrir les dépenses courantes
                    et les frais médicaux. Cela peut épuiser rapidement vos
                    économies, affectant potentiellement vos objectifs financiers
                    à long terme.
                  </p>
                </div>
              </div>

              {/* Accumulation of Debt */}
              <div className="bg-[#3f6f8f]/90 rounded-[32px] shadow-xl shadow-slate-900/30 px-6 sm:px-8 py-8 sm:py-10">
                <h4 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                  Accumulation de dettes :
                </h4>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 mt-1 text-[#00e0ff] shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    En l'absence d'assurance invalidité, les individus peuvent
                    recourir à l'emprunt ou aux cartes de crédit pour couvrir leurs
                    dépenses. L'accumulation de dettes peut entraîner une
                    instabilité financière et des difficultés à gérer les
                    remboursements.
                  </p>
                </div>
              </div>

              {/* Impact on Retirement Savings */}
              <div className="bg-[#3f6f8f]/90 rounded-[32px] shadow-xl shadow-slate-900/30 px-6 sm:px-8 py-8 sm:py-10">
                <h4 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                  Impact sur l'épargne-retraite :
                </h4>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 mt-1 text-[#00e0ff] shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    Utiliser l'épargne-retraite pour couvrir les dépenses courantes
                    pendant une période d'invalidité peut avoir des conséquences à
                    long terme. Cela peut affecter votre capacité à épargner pour
                    la retraite et entraîner une réduction du revenu de retraite.
                  </p>
                </div>
              </div>

              {/* Limited Access to Healthcare */}
              <div className="bg-[#3f6f8f]/90 rounded-[32px] shadow-xl shadow-slate-900/30 px-6 sm:px-8 py-8 sm:py-10">
                <h4 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                  Accès limité aux soins de santé :
                </h4>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 mt-1 text-[#00e0ff] shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    Les contraintes financières peuvent limiter votre accès aux
                    soins médicaux et traitements nécessaires. Retarder ou renoncer
                    aux soins médicaux peut aggraver les problèmes de santé et
                    entraver votre rétablissement.
                  </p>
                </div>
              </div>

              {/* Strain on Relationships */}
              <div className="bg-[#3f6f8f]/90 rounded-[32px] shadow-xl shadow-slate-900/30 px-6 sm:px-8 py-8 sm:py-10">
                <h4 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                  Tensions dans les relations :
                </h4>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 mt-1 text-[#00e0ff] shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    Le stress financier peut mettre à rude épreuve les relations
                    avec les membres de la famille, les amis et les collègues.
                    L'incapacité à contribuer financièrement à un foyer peut créer
                    des tensions supplémentaires.
                  </p>
                </div>
              </div>

              {/* Potential for Bankruptcy */}
              <div className="bg-[#3f6f8f]/90 rounded-[32px] shadow-xl shadow-slate-900/30 px-6 sm:px-8 py-8 sm:py-10">
                <h4 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                  Risque de faillite :
                </h4>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 mt-1 text-[#00e0ff] shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    Dans les cas extrêmes, la pression financière résultant de
                    l'absence d'assurance invalidité peut entraîner des dettes
                    insurmontables et un risque de faillite.
                  </p>
                </div>
              </div>

              {/* Reduced Quality of Life */}
              <div className="bg-[#3f6f8f]/90 rounded-[32px] shadow-xl shadow-slate-900/30 px-6 sm:px-8 py-8 sm:py-10">
                <h4 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                  Réduction de la qualité de vie :
                </h4>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 mt-1 text-[#00e0ff] shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    La qualité de vie globale peut souffrir sans le filet de
                    sécurité offert par l'assurance invalidité. Le stress lié à
                    l'incertitude financière et les défis de gestion des dépenses
                    quotidiennes peuvent affecter le bien-être mental et émotionnel.
                  </p>
                </div>
              </div>

              {/* Limited Future Income Replacement */}
              <div className="bg-[#3f6f8f]/90 rounded-[32px] shadow-xl shadow-slate-900/30 px-6 sm:px-10 py-8 sm:py-10 md:col-span-3">
                <h4 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                  Remplacement limité du revenu futur :
                </h4>
                <div className="flex items-start gap-3 max-w-5xl mx-auto">
                  <CheckCircle className="h-5 w-5 mt-1 text-[#00e0ff] shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    Si l'invalidité se prolonge, l'absence d'assurance invalidité
                    peut limiter votre capacité à remplacer le revenu perdu à
                    l'avenir. Reprendre le travail après une longue absence peut
                    s'avérer difficile.
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom text + CTA */}
            <p className="mt-10 text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-4xl mx-auto mb-8">
              Pour atténuer ces risques, les individus sont encouragés à envisager{" "}
              <span className="font-bold">l'assurance invalidité</span> dans le
              cadre de leur planification financière. L'assurance invalidité offre
              un filet de sécurité financier en remplaçant une partie du revenu
              perdu en cas d'invalidité, aidant les individus et leurs familles à
              maintenir leur stabilité financière pendant les périodes difficiles.
            </p>

            <div className="flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
              >
                Sécurisez votre revenu
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* OUR IMPACT BY THE NUMBERS */}
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className={container}>
          <div className="text-center text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-10">
              Notre impact en chiffres
            </h2>

            {/* Stats */}
            <div className="grid gap-8 md:grid-cols-3 mb-10">
              <div>
                <p className="text-3xl sm:text-4xl font-bold text-[#49628b] mb-2">
                  1 000+
                </p>
                <p className="text-base sm:text-lg text-gray-600">
                  Clients servis
                </p>
              </div>

              <div>
                <p className="text-3xl sm:text-4xl font-bold text-[#49628b] mb-2">
                  8 000+
                </p>
                <p className="text-base sm:text-lg text-gray-600">
                  Polices vendues
                </p>
              </div>

              <div>
                <p className="text-3xl sm:text-4xl font-bold text-[#49628b] mb-2">
                  50 millions $+
                </p>
                <p className="text-base sm:text-lg text-gray-600">
                  Couverture totale vendue
                </p>
              </div>
            </div>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 max-w-3xl mx-auto mb-10">
              Rejoignez les milliers de personnes qui ont sécurisé leur avenir
              financier avec{" "}
              <a
                href="/"
                className="text-[#00a8ff] font-semibold underline hover:text-[#0092e6] transition"
              >
                einsured.ca
              </a>
              . Explorez notre monde complet d'assurance invalidité, où l'expertise,
              l'empathie et l'innovation convergent pour votre sécurité.
            </p>

            {/* CTA */}
            <div className="flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
              >
                Réservez une réunion maintenant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Discover Unrivaled Excellence */}
      <section className="relative py-16 sm:py-20 md:py-24 bg-[#dce2ea]">
        <div className="absolute top-0 left-0 w-full h-[4px] bg-[#2b4672]" />
        <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#2b4672]" />

        <div className={`relative ${container}`}>
          <div className="text-[#27406b]">
            <h2 className="text-[#49628b] text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4">
              Découvrez l'excellence inégalée en assurance invalidité avec
              einsured.ca
            </h2>

            <p className="text-base sm:text-lg text-center leading-7 sm:leading-8 mb-8">
              Bienvenue chez einsured.ca - Votre destination ultime pour
              l'assurance invalidité au Canada.
            </p>

            <h3 className="text-[#49628b] text-xl sm:text-2xl md:text-3xl font-bold mb-6">
              Voici pourquoi nous nous distinguons comme le meilleur courtier, appuyé
              par des chiffres qui témoignent de notre engagement envers la sécurité
              financière.
            </h3>

            <ul className="space-y-4 text-base sm:text-lg leading-7 sm:leading-8 mb-10">
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="text-[#49628b] font-semibold">
                    Une expertise sur laquelle vous pouvez compter :
                  </span>{" "}
                  Chez einsured.ca, notre équipe compte des experts chevronnés en
                  assurance invalidité. Avec une expérience cumulée de plus de
                  12 ans, nous offrons des conseils d'experts adaptés à vos besoins
                  uniques, vous permettant de prendre des décisions éclairées.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="text-[#49628b] font-semibold">
                    Une multitude d'options :
                  </span>{" "}
                  Choisissez parmi un large éventail d'options d'assurance
                  invalidité adaptées à votre mode de vie et à vos objectifs
                  financiers. Notre vaste portefeuille auprès des meilleurs
                  fournisseurs garantit une police parfaitement alignée sur vos
                  besoins.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="text-[#49628b] font-semibold">
                    Solutions personnalisées pour chaque individu :
                  </span>{" "}
                  Votre parcours financier est unique, et votre couverture
                  d'assurance devrait l'être aussi. Nous nous spécialisons dans
                  la création de solutions personnalisées, tenant compte de facteurs
                  tels que les besoins de remplacement du revenu, les délais
                  d'attente et les durées des prestations.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="text-[#49628b] font-semibold">
                    Communication transparente :
                  </span>{" "}
                  La transparence est notre politique. Nous croyons en une
                  communication claire, vous permettant de comprendre les subtilités
                  de l'assurance invalidité. Prenez des décisions éclairées qui
                  renforcent votre sécurité financière.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="text-[#49628b] font-semibold">
                    Primes compétitives pour une valeur maximale :
                  </span>{" "}
                  Trouver l'équilibre parfait entre une couverture complète et
                  l'accessibilité est notre point fort. Bénéficiez de primes
                  compétitives qui vous assurent d'obtenir la meilleure valeur
                  pour votre investissement en assurance invalidité.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="text-[#49628b] font-semibold">
                    Partenariats de confiance avec les principaux fournisseurs :
                  </span>{" "}
                  Nos solides partenariats avec les principaux assureurs du Canada
                  garantissent l'accès à des compagnies d'assurance réputées, en
                  affaires depuis plus de 100 ans, offrant des options de couverture
                  fiables. Bénéficiez de la solidité financière d'assureurs bien
                  établis avec einsured.ca.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="text-[#49628b] font-semibold">
                    Témoignages et histoires de réussite :
                  </span>{" "}
                  De véritables histoires de réussite et des témoignages positifs de
                  nos clients soulignent l'impact d'einsured.ca dans la protection
                  des avenirs financiers. Explorez ces témoignages directs pour
                  comprendre notre engagement envers l'excellence.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="text-[#49628b] font-semibold">
                    Demande et approbation – En ligne :
                  </span>{" "}
                  Entrez dans l'ère numérique avec einsured.ca. Nos technologies
                  innovantes rendent le processus d'assurance simple et pratique.
                  De l'obtention de soumissions à leur comparaison avec plusieurs
                  compagnies d'assurance canadiennes réputées, notre plateforme en
                  ligne vous donne le contrôle.
                </p>
              </li>
            </ul>

            <div className="flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
              >
                Commencer
              </a>
            </div>
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
}
